import { ArrowLeft, ArrowRight, BookOpenText, Check, Headphones, LockKeyhole, MessageCircleMore } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { AudioPlayer } from '../components/AudioPlayer'
import { Comments } from '../components/Comments'
import { WaveArtwork } from '../components/WaveArtwork'
import { useApp } from '../state/AppContext'

export function ActivityPage() {
  const { activityId } = useParams()
  const { journey, progress, busy } = useApp()
  const [answer, setAnswer] = useState('')
  const [botelhoMode, setBotelhoMode] = useState<'listen' | 'read'>('listen')
  const activity = useMemo(() => journey?.activities.find((item) => item.id === activityId), [journey, activityId])
  const nextActivity = useMemo(() => journey?.activities.find((item) => item.order === (activity?.order ?? 0) + 1), [journey, activity])

  useEffect(() => { setAnswer(''); setBotelhoMode('listen') }, [activityId])
  useEffect(() => { if (activity?.status === 'available') void progress(activity.id, 'start') }, [activity?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!journey || !activity) return <Navigate to="/hoje" replace />
  if (activity.status === 'locked') return <main className="locked-page"><LockKeyhole /><p className="step-label">Ainda não</p><h1>A pausa também faz parte da música.</h1><p>Termine o encontro anterior para seguir para este. O ritmo é seu — mas quanto mais tempo você se dedicar a cada escuta, mais ela se revela.</p><Link className="button button--primary" to="/hoje"><ArrowLeft />Voltar para hoje</Link></main>

  const showCompletion = activity.status === 'completed'

  const complete = async () => {
    try { await progress(activity.id, 'complete', { answer }) } catch { /* contexto */ }
  }

  return <article className={`activity-page activity-page--${activity.kind}`}>
    <header className="activity-top"><Link to="/hoje" aria-label="Voltar"><ArrowLeft /></Link><span>{activity.eyebrow} · {activity.duration}</span><i>{activity.order}/3</i></header>
    <section className="activity-title"><p className="step-label">{journey.work.composer}</p><h1>{activity.title}</h1><p>{activity.content.intro}</p></section>
    <WaveArtwork compact gold={activity.kind === 'botelho'} />
    {activity.kind === 'botelho' ? (
      <section className="botelho-note">
        {botelhoMode === 'listen' ? (
          <div className="botelho-reveal">
            <img src="/images/leandro-botelho.png" alt="Leandro Botelho" className="botelho-avatar" />
            <p className="botelho-reveal__name">Leandro Botelho</p>
          </div>
        ) : (
          <div className="botelho-sign"><span>LB</span><b>Leandro Botelho</b></div>
        )}
        <div className="botelho-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={botelhoMode === 'listen'} className={botelhoMode === 'listen' ? 'is-active' : ''} onClick={() => setBotelhoMode('listen')}><Headphones />Ouvir</button>
          <button type="button" role="tab" aria-selected={botelhoMode === 'read'} className={botelhoMode === 'read' ? 'is-active' : ''} onClick={() => setBotelhoMode('read')}><BookOpenText />Ler transcrição</button>
        </div>
        {botelhoMode === 'listen' ? (
          <div className="listening-room listening-room--compact">
            <AudioPlayer src={journey.work.audioUrl} initialPosition={activity.progressSeconds} onPosition={(position) => void progress(activity.id, 'position', { position: Math.floor(position) })} />
            <p className="license-note">{journey.work.attribution} · {journey.work.license}</p>
          </div>
        ) : (
          <p>{activity.content.botelhoNote}</p>
        )}
      </section>
    ) : (
      <section className="listening-room">
        <div className="listening-heading"><span>{journey.work.subtitle}</span><small>{journey.work.duration}</small></div>
        <AudioPlayer src={journey.work.audioUrl} initialPosition={activity.progressSeconds} onPosition={(position) => void progress(activity.id, 'position', { position: Math.floor(position) })} />
        <p className="license-note">{journey.work.attribution} · {journey.work.license}</p>
      </section>
    )}
    {activity.content.transcript && <section className="listening-notes"><p className="step-label">Pontos de atenção</p><h2>Desafio da Escuta.</h2>{activity.content.transcript.map((note) => <div className="note-row" key={note.at}><button onClick={() => { const player = document.querySelector<HTMLAudioElement>('audio'); if (player) { player.currentTime = note.at; void player.play() } }}>{Math.floor(note.at / 60)}:{String(note.at % 60).padStart(2, '0')}</button><div><h3>{note.title}</h3><p>{note.text}</p></div></div>)}</section>}
    {!showCompletion && <section className="reflection-box"><MessageCircleMore /><p className="step-label">Antes de seguir</p><h2>{activity.content.prompt}</h2><textarea value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="Escreva do seu jeito. Uma palavra já serve." rows={4} /><p>Só você vê esta anotação.</p></section>}
    {!showCompletion && <footer className="activity-actions"><button className="button button--primary" disabled={busy || !answer.trim()} onClick={complete}>{busy ? 'Guardando…' : <>Concluir encontro<ArrowRight /></>}</button><Link to="/hoje">Continuar depois</Link></footer>}
    {showCompletion && <section className="pause-choice">
      <Check />
      <p className="step-label">Encontro concluído</p>
      <h2>A pausa também faz parte da música.</h2>
      <p>Quanto mais você se dedicar a esta escuta ativa, mais ela se revela — por isso recomendamos viver cada obra ao longo de uma semana. Mas o ritmo é seu: se preferir, siga direto para o próximo encontro.</p>
      <div className="pause-choice__actions">
        <Link className="button button--outline" to="/hoje">Fazer uma pausa por agora</Link>
        {nextActivity
          ? <Link className="button button--primary" to={`/atividade/${nextActivity.id}`}>Seguir agora<ArrowRight /></Link>
          : <Link className="button button--primary" to="/partitura">Ir para sua partitura<ArrowRight /></Link>}
      </div>
    </section>}
    {showCompletion && <Comments activityId={activity.id} />}
  </article>
}
